import type { GetMeUserResponse } from "@nlc/shared-types";

import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { ConfirmationModal } from "@/components/confirmation-modal";
import { toast } from "@/components/toast/toast-queue";
import { usersKeys } from "@/hooks/queryKeys";

import { useArchiveUser } from "../hooks/useArchiveUser";

export default function ArchiveUser({
  onClose,
  openModal,
  userdata,
}: {
  onClose: () => void;
  openModal: boolean;
  userdata: GetMeUserResponse | undefined;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isOpenModal, setIsOpenModal] = useState(false);
  const user = userdata?.user;
  const { isPending, mutate } = useArchiveUser(user?.id);

  useEffect(() => {
    if (openModal) {
      setIsOpenModal(true);
    }
    if (!isOpenModal) onClose();
  }, [openModal, isOpenModal]);

  if (!userdata || !user) {
    return null;
  }

  const archiveUser = () => {
    mutate(undefined, {
      onError: (error) => {
        setIsOpenModal(false);
        toast.add({
          description: error.message || "Please try again.",
          timeout: 5000,
          title: "Error archiving user",
          type: "error",
        });
      },
      onSuccess: () => {
        toast.add({
          description: `User ${user.name} was successfully archived.`,
          timeout: 5000,
          title: "User archived",
          type: "success",
        });

        navigate({ to: "/users" });

        return queryClient.invalidateQueries({
          queryKey: usersKeys.all,
        });
      },
    });
  };

  return (
    <ConfirmationModal
      confirmButtonColor="color"
      confirmButtonText="Archive"
      isConfirmLoading={isPending}
      isOpen={isOpenModal}
      onConfirm={archiveUser}
      onOpenChange={setIsOpenModal}
      text={`Are you sure you want to archive user ${user.name} - ${user.email}?`}
      title={`Archive user: ${user.name}`}
    />
  );
}
